import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsClock } from 'react-icons/bs';

import Card from '#globalComponents/Card/Card';
import Switch from '#globalComponents/Forms/Switch';
import TextInput from '#globalComponents/Forms/TextInput';

const WEEKDAYS = [
	{ value: 1, label: 'Montag', short: 'Mo' },
	{ value: 2, label: 'Dienstag', short: 'Di' },
	{ value: 3, label: 'Mittwoch', short: 'Mi' },
	{ value: 4, label: 'Donnerstag', short: 'Do' },
	{ value: 5, label: 'Freitag', short: 'Fr' },
	{ value: 6, label: 'Samstag', short: 'Sa' },
	{ value: 7, label: 'Sonntag', short: 'So' },
];

const ScheduleTab = ({ sendingSchedule, onScheduleChange }) => {
	const [enabled, setEnabled] = useState(sendingSchedule?.enabled || false);
	const [timeWindows, setTimeWindows] = useState(() => {
		const initial = sendingSchedule?.timeWindows || [];
		// Initialize with default weekday schedule if enabled but no windows
		if (sendingSchedule?.enabled && initial.length === 0) {
			return [1, 2, 3, 4, 5].map((day) => ({
				day,
				startTime: '09:00',
				endTime: '17:00',
			}));
		}
		return initial;
	});

	const handleToggleDay = (day) => {
		const existingWindow = timeWindows.find((w) => w.day === day);

		let newTimeWindows;
		if (existingWindow) {
			// Remove this day
			newTimeWindows = timeWindows.filter((w) => w.day !== day);
		} else {
			// Add this day with default times
			newTimeWindows = [...timeWindows, { day, startTime: '09:00', endTime: '17:00' }].sort(
				(a, b) => a.day - b.day,
			);
		}
		setTimeWindows(newTimeWindows);

		// Automatically notify parent of changes
		onScheduleChange({
			enabled,
			timeWindows: enabled ? newTimeWindows : [],
		});
	};

	const handleTimeChange = (day, field, value) => {
		const newTimeWindows = timeWindows.map((w) => (w.day === day ? { ...w, [field]: value } : w));
		setTimeWindows(newTimeWindows);

		// Automatically notify parent of changes
		onScheduleChange({
			enabled,
			timeWindows: enabled ? newTimeWindows : [],
		});
	};

	const handleToggleEnabled = (newEnabled) => {
		setEnabled(newEnabled);

		let newTimeWindows = timeWindows;
		if (newEnabled && timeWindows.length === 0) {
			// Create default weekday schedule immediately so it's included in the save
			newTimeWindows = [1, 2, 3, 4, 5].map((day) => ({
				day,
				startTime: '09:00',
				endTime: '17:00',
			}));
			setTimeWindows(newTimeWindows);
		}

		onScheduleChange({
			enabled: newEnabled,
			timeWindows: newEnabled ? newTimeWindows : [],
		});
	};

	const isDayActive = (day) => timeWindows.some((w) => w.day === day);

	return (
		<Card>
			<div className="flex flex-col space-y-1.5 mb-6">
				<div className="flex items-center gap-2">
					<BsClock className="h-5 w-5 text-bgBase-800" />
					<h3 className="text-lg font-semibold leading-none tracking-tight">Versandzeitplan</h3>
				</div>
				<p className="text-sm text-bgBase-800">
					Legen Sie fest, an welchen Tagen und zu welchen Uhrzeiten E-Mails versendet werden dürfen
				</p>
			</div>

			<div className="space-y-6">
				{/* Enable/Disable Toggle */}
				<div className="flex items-center justify-between p-4 bg-bgBase-300 rounded-card">
					<div>
						<h4 className="text-md font-medium">Zeitplan aktivieren</h4>
						<p className="text-sm text-bgBase-800">
							E-Mails werden nur innerhalb der festgelegten Zeitfenster versendet
						</p>
					</div>
					<Switch checked={enabled} onChange={handleToggleEnabled} />
				</div>

				{enabled && (
					<>
						{/* Weekday Selection */}
						<div className="space-y-3">
							<h4 className="text-md font-medium">Versandtage auswählen</h4>
							<div className="grid grid-cols-7 gap-2">
								{WEEKDAYS.map(({ value, label, short }) => (
									<button
										key={value}
										type="button"
										onClick={() => handleToggleDay(value)}
										className={`flex flex-col items-center justify-center p-3 rounded-card border-2 transition-all ${
											isDayActive(value)
												? 'border-primary-600 bg-primary-100 text-primary-700'
												: 'border-bgBase-500 bg-white text-text hover:border-bgBase-600'
										}`}
									>
										<span className="text-xs font-medium hidden sm:block">{label}</span>
										<span className="text-xs font-medium sm:hidden">{short}</span>
									</button>
								))}
							</div>
						</div>

						{/* Time Windows */}
						{timeWindows.length > 0 && (
							<div className="space-y-3">
								<h4 className="text-md font-medium">Uhrzeiten festlegen</h4>
								<div className="space-y-3">
									{timeWindows.map((window) => {
										const weekday = WEEKDAYS.find((d) => d.value === window.day);
										return (
											<div
												key={window.day}
												className="flex items-center gap-4 p-3 bg-bgBase-300 rounded-card"
											>
												<div className="w-24 text-sm font-medium text-text">
													{weekday?.label}
												</div>
												<div className="flex items-center gap-2 flex-1">
													<div className="flex items-center gap-2">
														<label className="text-sm text-text">Von</label>
														<TextInput
															type="time"
															value={window.startTime}
															onChange={(e) =>
																handleTimeChange(
																	window.day,
																	'startTime',
																	e.target.value,
																)
															}
															className="!w-36"
														/>
													</div>
													<div className="flex items-center gap-2">
														<label className="text-sm text-text">Bis</label>
														<TextInput
															type="time"
															value={window.endTime}
															onChange={(e) =>
																handleTimeChange(window.day, 'endTime', e.target.value)
															}
															className="!w-36"
														/>
													</div>
												</div>
											</div>
										);
									})}
								</div>
							</div>
						)}

						{timeWindows.length === 0 && (
							<div className="text-center py-8 text-bgBase-800">
								<BsClock className="h-12 w-12 mx-auto mb-3 text-bgBase-800" />
								<p className="text-sm">Wählen Sie mindestens einen Versandtag aus</p>
							</div>
						)}
					</>
				)}

				{!enabled && (
					<div className="text-center py-8 text-bgBase-800">
						<BsClock className="h-12 w-12 mx-auto mb-3 text-bgBase-800" />
						<p className="text-sm">
							Zeitplan ist deaktiviert - E-Mails werden werktags (Mo–Fr, 08:00–18:00) gleichmäßig über den
							Tag verteilt versendet
						</p>
					</div>
				)}
			</div>
		</Card>
	);
};

ScheduleTab.propTypes = {
	sendingSchedule: PropTypes.shape({
		enabled: PropTypes.bool,
		timeWindows: PropTypes.arrayOf(
			PropTypes.shape({
				day: PropTypes.number.isRequired,
				startTime: PropTypes.string.isRequired,
				endTime: PropTypes.string.isRequired,
			}),
		),
	}),
	onScheduleChange: PropTypes.func.isRequired,
};

ScheduleTab.defaultProps = {
	sendingSchedule: {
		enabled: false,
		timeWindows: [],
	},
};

export default ScheduleTab;
